import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, ChevronDown, Check, User, Building, Shield } from 'lucide-react';
import { UserProfile } from '../../types';
import { sanitizeMemberId } from '../../utils/formatters';

export interface MemberSelectComboboxProps {
  members: UserProfile[];
  selectedMemberId: string;
  onSelect: (memberId: string, member: UserProfile | null) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  id?: string;
  helperText?: string;
}

/**
 * Robust fuzzy search scoring algorithm.
 * Matches across:
 * - Name (highest weight)
 * - Member ID (raw & sanitized e.g. ASMM-001)
 * - IC No (formatted and digits-only)
 * - Phone (formatted and digits-only)
 * - Role & Gelanggang
 * - Bengkung
 */
export function scoreMemberFuzzy(query: string, member: UserProfile): { score: number; isMatch: boolean } {
  if (!query) return { score: 1, isMatch: true };

  const q = query.toLowerCase().trim();
  const qClean = q.replace(/[^a-z0-9]/g, '');
  const qDigits = q.replace(/[^0-9]/g, '');

  const name = (member.name || '').toLowerCase();
  const idRaw = (member.id || '').toLowerCase();
  const idSanitized = sanitizeMemberId(member.id).toLowerCase();
  const icNo = (member.icNo || '').toLowerCase();
  const icClean = icNo.replace(/[^0-9]/g, '');
  const phone = (member.phone || '').toLowerCase();
  const phoneClean = phone.replace(/[^0-9]/g, '');
  const role = (member.role || '').toLowerCase();
  const gelanggang = (member.gelanggang || '').toLowerCase();
  const bengkung = (member.bengkung || '').toLowerCase();

  let maxScore = 0;

  // 1. Exact matches
  if (name === q) return { score: 2000, isMatch: true };
  if (idRaw === q || idSanitized === q) return { score: 1800, isMatch: true };
  if (icClean && qDigits && qDigits.length >= 6 && icClean === qDigits) return { score: 1700, isMatch: true };
  if (phoneClean && qDigits && qDigits.length >= 7 && phoneClean === qDigits) return { score: 1650, isMatch: true };

  // 2. Starts With matches
  if (name.startsWith(q)) maxScore = Math.max(maxScore, 1300);
  if (idSanitized.startsWith(q) || idRaw.startsWith(q)) maxScore = Math.max(maxScore, 1200);

  // 3. Word-start match on name (e.g. searching "Hafiz" for "Mohd Hafiz")
  const nameWords = name.split(/[\s\-_/()]+/).filter(Boolean);
  if (nameWords.some(w => w.startsWith(q))) {
    maxScore = Math.max(maxScore, 1100);
  }

  // 4. Substring match
  const nameSubIdx = name.indexOf(q);
  if (nameSubIdx !== -1) {
    const isWordStart = nameSubIdx === 0 || /[\s\-_/()]/.test(name[nameSubIdx - 1]);
    maxScore = Math.max(maxScore, (isWordStart ? 850 : 500) - nameSubIdx);
  }

  if (idSanitized.includes(q) || idRaw.includes(q)) {
    maxScore = Math.max(maxScore, 650);
  }

  // IC & Phone substring matching - only when at least 3 digits provided
  if (icNo.includes(q) || (qDigits.length >= 3 && icClean.includes(qDigits))) {
    maxScore = Math.max(maxScore, 600);
  }

  if (phone.includes(q) || (qDigits.length >= 3 && phoneClean.includes(qDigits))) {
    maxScore = Math.max(maxScore, 450);
  }

  // Role, Gelanggang, Bengkung matching - only for queries with at least 3 characters
  if (q.length >= 3) {
    if (role.includes(q)) {
      maxScore = Math.max(maxScore, 350);
    }
    if (gelanggang.includes(q)) {
      maxScore = Math.max(maxScore, 300);
    }
    if (bengkung.includes(q)) {
      maxScore = Math.max(maxScore, 250);
    }
  }

  // 5. Multi-token match (e.g. "hafiz melaka" or "ali pusaka" or "azman 012")
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    const combinedStr = `${name} ${idRaw} ${idSanitized} ${icNo} ${phone} ${role} ${gelanggang} ${bengkung}`;
    const allTokensFound = tokens.every(tok => combinedStr.includes(tok));
    if (allTokensFound) {
      maxScore = Math.max(maxScore, 950);
    }
  }

  // 6. Sequential fuzzy character match (subsequence) on name
  let qIdx = 0;
  let charScore = 0;
  let prevIdx = -2;

  for (let i = 0; i < name.length && qIdx < q.length; i++) {
    if (name[i] === q[qIdx]) {
      if (i === prevIdx + 1) charScore += 20; // consecutive match bonus
      if (i === 0 || /[\s\-_/()]/.test(name[i - 1])) charScore += 30; // word start bonus
      else charScore += 3;
      prevIdx = i;
      qIdx++;
    }
  }

  if (qIdx === q.length && q.length >= 2) {
    maxScore = Math.max(maxScore, 200 + charScore);
  }

  return {
    score: maxScore,
    isMatch: maxScore > 0,
  };
}

export const MemberSelectCombobox: React.FC<MemberSelectComboboxProps> = ({
  members,
  selectedMemberId,
  onSelect,
  placeholder = 'Cari nama, No. IC, ID pesilat, gelanggang...',
  label,
  required = false,
  disabled = false,
  allowClear = false,
  className = '',
  id,
  helperText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Active selected member object
  const selectedMember = useMemo(() => {
    return members.find(m => m.id === selectedMemberId) || null;
  }, [members, selectedMemberId]);

  // Fuzzy filter and sort candidates
  const filteredCandidates = useMemo(() => {
    if (!searchQuery.trim()) {
      return members;
    }

    const scored = members
      .map(member => {
        const { score, isMatch } = scoreMemberFuzzy(searchQuery, member);
        return { member, score, isMatch };
      })
      .filter(item => item.isMatch)
      .sort((a, b) => b.score - a.score);

    return scored.map(item => item.member);
  }, [members, searchQuery]);

  // Click outside to close dropdown
  useEffect(() => {
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('touchstart', handlePointerDown);
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-member-item]');
    const activeEl = items[highlightedIndex] as HTMLElement | undefined;
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const handleSelectMember = (member: UserProfile) => {
    onSelect(member.id, member);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('', null);
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < filteredCandidates.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCandidates.length > 0 && filteredCandidates[highlightedIndex]) {
        handleSelectMember(filteredCandidates[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label htmlFor={id} className="block text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
          <span>
            {label} {required && <span className="text-asmm-red">*</span>}
          </span>
          {selectedMember && (
            <span className="text-[10px] text-slate-500 font-mono font-medium">
              ID: {sanitizeMemberId(selectedMember.id)}
            </span>
          )}
        </label>
      )}

      {/* Main Trigger Card / Button */}
      <div
        id={id}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full text-left rounded-xl border transition-all cursor-pointer select-none ${
          disabled
            ? 'bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed'
            : isOpen
            ? 'bg-white border-asmm-red ring-2 ring-red-100/80 shadow-xs'
            : selectedMember
            ? 'bg-white border-slate-300 hover:border-asmm-red/70 shadow-2xs'
            : 'bg-slate-50 hover:bg-white border-slate-200 hover:border-slate-300'
        }`}
      >
        {selectedMember ? (
          <div className="p-2 sm:p-2.5 flex items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              {/* Member Avatar */}
              <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                {selectedMember.photoUrl ? (
                  <img
                    src={selectedMember.photoUrl}
                    alt={selectedMember.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs font-black text-slate-700 uppercase">
                    {(selectedMember.name || 'P').charAt(0)}
                  </span>
                )}
              </div>

              {/* Member Details */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-extrabold text-xs text-slate-900 truncate">
                    {selectedMember.name}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200">
                    {sanitizeMemberId(selectedMember.id)}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 font-medium flex items-center gap-1.5 mt-0.5 truncate">
                  <span className="font-bold text-slate-700 bg-amber-50 text-amber-900 px-1.5 py-0.2 rounded border border-amber-200/80">
                    {selectedMember.role || 'Ahli'}
                  </span>
                  <span>•</span>
                  <span className="truncate">{selectedMember.gelanggang || 'Pusaka Warisan'}</span>
                  {selectedMember.icNo && (
                    <>
                      <span>•</span>
                      <span className="font-mono">IC: {selectedMember.icNo}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0">
              {allowClear && !disabled && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                  title="Padam pilihan ahli"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <span className="text-[11px] font-bold text-asmm-red bg-red-50 hover:bg-red-100 px-2 py-1 rounded-lg border border-red-100 transition-colors hidden sm:inline-block">
                Tukar
              </span>
              <ChevronDown
                className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                  isOpen ? 'rotate-180 text-asmm-red' : ''
                }`}
              />
            </div>
          </div>
        ) : (
          <div className="px-3.5 py-2.5 flex items-center justify-between text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="font-medium text-slate-500">{placeholder}</span>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          </div>
        )}
      </div>

      {helperText && !isOpen && (
        <p className="text-[11px] text-slate-500 mt-1">{helperText}</p>
      )}

      {/* Floating Fuzzy Search Popover */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1.5 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden animate-fade-in flex flex-col max-h-80">
          {/* Search Input Bar */}
          <div className="p-2.5 bg-slate-50/90 border-b border-slate-200/80 shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Taip carian fuzzy: nama, No. IC, ID, gelanggang..."
                className="w-full pl-9 pr-20 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red focus:ring-2 focus:ring-red-100 placeholder:text-slate-400"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"
                    title="Padam carian"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                  {filteredCandidates.length}
                </span>
              </div>
            </div>
          </div>

          {/* Candidates Scrollable List */}
          <div ref={listRef} className="overflow-y-auto divide-y divide-slate-100 custom-scrollbar flex-1">
            {filteredCandidates.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-xs font-bold text-slate-700">Tiada ahli ditemui</p>
                <p className="text-[11px] text-slate-500 mt-1 max-w-xs mx-auto">
                  Tiada padanan ahli/pesilat untuk carian "{searchQuery}". Cuba gunakan sebahagian nama, nombor IC atau ID ahli.
                </p>
              </div>
            ) : (
              filteredCandidates.map((member, index) => {
                const isSelected = member.id === selectedMemberId;
                const isHighlighted = index === highlightedIndex;

                return (
                  <button
                    key={member.id}
                    type="button"
                    data-member-item
                    onClick={() => handleSelectMember(member)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full px-3.5 py-2.5 text-left text-xs transition-colors flex items-center justify-between gap-3 cursor-pointer ${
                      isSelected
                        ? 'bg-red-50/70 text-slate-900 font-bold border-l-4 border-asmm-red'
                        : isHighlighted
                        ? 'bg-slate-50 text-slate-900'
                        : 'bg-white hover:bg-slate-50/70 text-slate-800'
                    }`}
                  >
                    {/* Left: Avatar & Info */}
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                        {member.photoUrl ? (
                          <img
                            src={member.photoUrl}
                            alt={member.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xs font-black text-slate-600 uppercase">
                            {(member.name || 'P').charAt(0)}
                          </span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-extrabold text-xs text-slate-900 truncate">
                            {member.name}
                          </span>
                          <span className="text-[10px] font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200">
                            {sanitizeMemberId(member.id)}
                          </span>
                        </div>

                        <div className="text-[10px] text-slate-500 font-medium flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <span className="font-bold text-amber-900 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200">
                            {member.role || 'Ahli'}
                          </span>
                          <span>•</span>
                          <span className="text-slate-600">{member.gelanggang || 'Pusaka Warisan'}</span>
                          {member.icNo && (
                            <>
                              <span>•</span>
                              <span className="font-mono text-slate-500">IC: {member.icNo}</span>
                            </>
                          )}
                          {member.phone && (
                            <>
                              <span>•</span>
                              <span className="font-mono text-slate-500">Tel: {member.phone}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Checkmark for Selected */}
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-asmm-red text-white flex items-center justify-center shrink-0 shadow-xs">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Quick Footer */}
          <div className="px-3.5 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500 flex items-center justify-between shrink-0">
            <span>
              Jumlah Calon: <strong className="text-slate-800">{filteredCandidates.length}</strong> / {members.length}
            </span>
            <span className="text-slate-400">Tekan Enter untuk pilih • Esc untuk tutup</span>
          </div>
        </div>
      )}
    </div>
  );
};
